import { useState } from 'react';

import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  changeAllRetailEvotorProductStatuses,
  checkRetailEvotorProductsReadyForExport,
  deleteAllRetailEvotorStoreProductGroups,
  deleteAllRetailEvotorStoreProducts,
  exportAllRetailEvotorProductGroups,
  exportAllRetailEvotorProducts,
  importRetailEvotorDevices,
  importRetailEvotorDocuments,
  importRetailEvotorEmployees,
  importRetailEvotorRoles,
  importRetailEvotorStoreProductGroups,
  importRetailEvotorStoreProducts,
  importRetailEvotorStores,
} from '../../api/retailEvotorApi';
import { settingsKeys } from '../../hooks';
import type {
  RetailEvotorMasterDataStatus,
  RetailEvotorStoreDocumentType,
} from '../../model/settingsTypes';
import { SettingsCard } from './SettingsCard';
import { SyncTriggerRow } from './SyncTriggerRow';

const DOC_TYPES: RetailEvotorStoreDocumentType[] = [
  'SELL',
  'PAYBACK',
  'BUY',
  'BUYBACK',
  'OPEN_SESSION',
  'POS_OPEN_SESSION',
  'CLOSE_SESSION',
  'CASH_INCOME',
  'CASH_OUTCOME',
  'INVENTORY',
  'ACCEPT',
  'REVALUATION',
  'WRITE_OFF',
  'OPEN_TARE',
  'RETURN',
  'X_REPORT',
  'Z_REPORT',
  'CORRECTION',
  'CORRECTION_V2',
];

const STATUS_VALUES: RetailEvotorMasterDataStatus[] = [
  'READY_FOR_EXPORT',
  'NOT_NEEDED',
  'EXPORTING',
  'EXPORTED',
  'ERROR',
  'WAIT',
];

function statusOptionKey(value: RetailEvotorMasterDataStatus): string {
  const map: Record<RetailEvotorMasterDataStatus, string> = {
    READY_FOR_EXPORT: 'readyForExport',
    NOT_NEEDED: 'notNeeded',
    EXPORTING: 'exporting',
    EXPORTED: 'exported',
    ERROR: 'error',
    WAIT: 'wait',
  };
  return map[value];
}

/** A small "ISO string, or omit" bridge for `<TextField type="datetime-local">` values. */
function toIso(localValue: string): string | undefined {
  if (!localValue) return undefined;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

const DATE_TIME_INPUT_PROPS = { inputLabel: { shrink: true } } as const;

export interface RetailEvotorSyncPanelProps {
  canManage: boolean;
}

/**
 * Синхронизация tab — merges the former Export/Import tabs into one, in journey order: **«Загрузить
 * из Эвотор»** (upstream → platform) above **«Выгрузить в Эвотор»** (platform → upstream), with the
 * dev-only destructive delete-all operations collapsed into their own section at the bottom.
 *
 * As of adr-integration-012/adr-integration-013 (2026-08-30) every trigger here is tenant-scoped,
 * so ordinary rows fire immediately with no confirmation — only the two delete-all rows still ask
 * first (`destructive`, with a tenant-scoped "this is irreversible" warning, not the old "affects
 * every merchant" one). Every row shows its own inline result `Alert` (success or error) rather
 * than a page-level snackbar, so firing several triggers in a row never clobbers a previous result.
 */
export function RetailEvotorSyncPanel({ canManage }: RetailEvotorSyncPanelProps) {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<RetailEvotorMasterDataStatus | ''>('');
  const statusLabel = (value: RetailEvotorMasterDataStatus | '') =>
    value ? t(`retailSettings.sync.statusOptions.${statusOptionKey(value)}`) : '';

  const [productIds, setProductIds] = useState('');
  const [productsSince, setProductsSince] = useState('');
  const [productsIncludeRemoved, setProductsIncludeRemoved] = useState(false);

  const [groupIds, setGroupIds] = useState('');
  const [groupsSince, setGroupsSince] = useState('');
  const [groupsIncludeRemoved, setGroupsIncludeRemoved] = useState(false);

  const [docsFrom, setDocsFrom] = useState('');
  const [docsUntil, setDocsUntil] = useState('');
  const [docType, setDocType] = useState<RetailEvotorStoreDocumentType | ''>('');

  const paramFieldsSx = { width: '100%', maxWidth: 420 } as const;

  // These two triggers repopulate a list this page shows elsewhere (the Магазины/Кассы tabs), so —
  // like `useImportRetailEvotorStores`/`useImportRetailEvotorDevices` — invalidate that list's
  // query on success. Composed here as plain functions (rather than the invalidating hooks
  // directly) so `SyncTriggerRow` keeps owning its own single mutation/result state per row.
  const runStoresImport = async () => {
    const message = await importRetailEvotorStores();
    void queryClient.invalidateQueries({ queryKey: settingsKeys.retailEvotorStoresRoot });
    return message;
  };
  const runDevicesImport = async () => {
    const message = await importRetailEvotorDevices();
    void queryClient.invalidateQueries({ queryKey: settingsKeys.retailEvotorDevicesRoot });
    return message;
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('retailSettings.sync.loadSection.heading')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {t('retailSettings.sync.loadSection.description')}
        </Typography>

        <Stack spacing={2.5}>
          <SettingsCard
            title={t('retailSettings.sync.loadSection.storesCardTitle')}
            icon={<StorefrontRoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.storesLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.storesButton')}
              run={runStoresImport}
              disabled={!canManage}
            />
          </SettingsCard>

          <SettingsCard
            title={t('retailSettings.sync.loadSection.storeProductsCardTitle')}
            description={t('retailSettings.sync.loadSection.storeProductsCardDescription')}
            icon={<Inventory2RoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.importProductsLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.importProductsButton')}
              run={() =>
                importRetailEvotorStoreProducts({
                  productEvotorIds: productIds.trim() || undefined,
                  isRemoved: productsIncludeRemoved,
                  sinceDateTime: toIso(productsSince),
                })
              }
              disabled={!canManage}
            >
              <Stack spacing={1.5} sx={paramFieldsSx}>
                <TextField
                  size="small"
                  label={t('retailSettings.sync.loadSection.evotorIdsLabel')}
                  helperText={t('retailSettings.sync.loadSection.evotorIdsHelper')}
                  value={productIds}
                  onChange={(event) => setProductIds(event.target.value)}
                  disabled={!canManage}
                  slotProps={{ htmlInput: { sx: { fontFamily: 'JetBrains Mono, monospace' } } }}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label={t('retailSettings.sync.loadSection.sinceLabel')}
                  value={productsSince}
                  onChange={(event) => setProductsSince(event.target.value)}
                  disabled={!canManage}
                  slotProps={DATE_TIME_INPUT_PROPS}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={productsIncludeRemoved}
                      onChange={(event) => setProductsIncludeRemoved(event.target.checked)}
                      disabled={!canManage}
                    />
                  }
                  label={t('retailSettings.sync.loadSection.includeRemovedLabel')}
                />
              </Stack>
            </SyncTriggerRow>
          </SettingsCard>

          <SettingsCard
            title={t('retailSettings.sync.loadSection.storeGroupsCardTitle')}
            icon={<Inventory2RoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.importGroupsLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.importGroupsButton')}
              run={() =>
                importRetailEvotorStoreProductGroups({
                  productGroupEvotorIds: groupIds.trim() || undefined,
                  isRemoved: groupsIncludeRemoved,
                  sinceDateTime: toIso(groupsSince),
                })
              }
              disabled={!canManage}
            >
              <Stack spacing={1.5} sx={paramFieldsSx}>
                <TextField
                  size="small"
                  label={t('retailSettings.sync.loadSection.evotorIdsLabel')}
                  helperText={t('retailSettings.sync.loadSection.evotorIdsHelper')}
                  value={groupIds}
                  onChange={(event) => setGroupIds(event.target.value)}
                  disabled={!canManage}
                  slotProps={{ htmlInput: { sx: { fontFamily: 'JetBrains Mono, monospace' } } }}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label={t('retailSettings.sync.loadSection.sinceLabel')}
                  value={groupsSince}
                  onChange={(event) => setGroupsSince(event.target.value)}
                  disabled={!canManage}
                  slotProps={DATE_TIME_INPUT_PROPS}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={groupsIncludeRemoved}
                      onChange={(event) => setGroupsIncludeRemoved(event.target.checked)}
                      disabled={!canManage}
                    />
                  }
                  label={t('retailSettings.sync.loadSection.includeRemovedLabel')}
                />
              </Stack>
            </SyncTriggerRow>
          </SettingsCard>

          <SettingsCard
            title={t('retailSettings.sync.loadSection.staffCardTitle')}
            icon={<GroupsRoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.employeesLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.employeesButton')}
              run={importRetailEvotorEmployees}
              disabled={!canManage}
            />
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.rolesLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.rolesButton')}
              run={importRetailEvotorRoles}
              disabled={!canManage}
            />
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.devicesLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.devicesButton')}
              run={runDevicesImport}
              disabled={!canManage}
            />
          </SettingsCard>

          <SettingsCard
            title={t('retailSettings.sync.loadSection.documentsCardTitle')}
            description={t('retailSettings.sync.loadSection.documentsCardDescription')}
            icon={<ReceiptLongRoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.loadSection.documentsLabel')}
              buttonLabel={t('retailSettings.sync.loadSection.documentsButton')}
              run={() =>
                importRetailEvotorDocuments({
                  sinceDateTime: toIso(docsFrom),
                  untilDateTime: toIso(docsUntil),
                  storeDocumentType: docType || undefined,
                })
              }
              disabled={!canManage}
            >
              <Stack spacing={1.5} sx={paramFieldsSx}>
                <TextField
                  size="small"
                  type="datetime-local"
                  label={t('retailSettings.sync.loadSection.fromLabel')}
                  value={docsFrom}
                  onChange={(event) => setDocsFrom(event.target.value)}
                  disabled={!canManage}
                  slotProps={DATE_TIME_INPUT_PROPS}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label={t('retailSettings.sync.loadSection.untilLabel')}
                  value={docsUntil}
                  onChange={(event) => setDocsUntil(event.target.value)}
                  disabled={!canManage}
                  slotProps={DATE_TIME_INPUT_PROPS}
                />
                <TextField
                  select
                  size="small"
                  label={t('retailSettings.sync.loadSection.docTypeLabel')}
                  value={docType}
                  onChange={(event) =>
                    setDocType(event.target.value as RetailEvotorStoreDocumentType | '')
                  }
                  disabled={!canManage}
                >
                  <MenuItem value="">{t('retailSettings.sync.loadSection.docTypeAll')}</MenuItem>
                  {DOC_TYPES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`retailSettings.sync.loadSection.docTypes.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </SyncTriggerRow>
          </SettingsCard>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('retailSettings.sync.exportSection.heading')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {t('retailSettings.sync.exportSection.description')}
        </Typography>

        <Stack spacing={2.5}>
          <SettingsCard
            title={t('retailSettings.sync.exportSection.catalogCardTitle')}
            description={t('retailSettings.sync.exportSection.catalogCardDescription')}
            icon={<IosShareRoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.exportSection.allProductsLabel')}
              description={t('retailSettings.sync.exportSection.allProductsDescription')}
              buttonLabel={t('retailSettings.sync.exportSection.allProductsButton')}
              icon={<IosShareRoundedIcon />}
              run={exportAllRetailEvotorProducts}
              disabled={!canManage}
            />
            <SyncTriggerRow
              label={t('retailSettings.sync.exportSection.allGroupsLabel')}
              buttonLabel={t('retailSettings.sync.exportSection.allGroupsButton')}
              icon={<IosShareRoundedIcon />}
              run={exportAllRetailEvotorProductGroups}
              disabled={!canManage}
            />
          </SettingsCard>

          <SettingsCard
            title={t('retailSettings.sync.exportSection.maintenanceCardTitle')}
            description={t('retailSettings.sync.exportSection.maintenanceCardDescription')}
            icon={<BuildRoundedIcon />}
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.exportSection.readyCheckLabel')}
              description={t('retailSettings.sync.exportSection.readyCheckDescription')}
              buttonLabel={t('retailSettings.sync.exportSection.readyCheckButton')}
              run={checkRetailEvotorProductsReadyForExport}
              disabled={!canManage}
            />
            <SyncTriggerRow
              label={t('retailSettings.sync.exportSection.statusAllLabel')}
              description={t('retailSettings.sync.exportSection.statusAllDescription')}
              buttonLabel={t('retailSettings.sync.exportSection.statusAllButton')}
              run={() => changeAllRetailEvotorProductStatuses(status as RetailEvotorMasterDataStatus)}
              disabled={!canManage || !status}
            >
              <TextField
                select
                size="small"
                label={t('retailSettings.sync.exportSection.statusSelectLabel')}
                value={status}
                onChange={(event) => setStatus(event.target.value as RetailEvotorMasterDataStatus)}
                disabled={!canManage}
                sx={{ minWidth: 220 }}
                helperText={
                  status
                    ? t('retailSettings.sync.exportSection.statusAllHelper', {
                        status: statusLabel(status),
                      })
                    : undefined
                }
              >
                {STATUS_VALUES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`retailSettings.sync.exportSection.statusOptions.${statusOptionKey(value)}`)}
                  </MenuItem>
                ))}
              </TextField>
            </SyncTriggerRow>
          </SettingsCard>
        </Stack>
      </Box>

      <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ScienceRoundedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={700}>
              {t('retailSettings.sync.technicalSection.heading')}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('retailSettings.sync.technicalSection.description')}
          </Typography>
          <SettingsCard
            title={t('retailSettings.sync.technicalSection.cardTitle')}
            divided
          >
            <SyncTriggerRow
              label={t('retailSettings.sync.technicalSection.deleteProductsLabel')}
              buttonLabel={t('retailSettings.sync.technicalSection.deleteProductsButton')}
              destructive
              icon={<DeleteForeverRoundedIcon />}
              confirm={{
                title: t('retailSettings.sync.technicalSection.deleteProductsConfirmTitle'),
                warning: t('retailSettings.sync.technicalSection.deleteWarning'),
              }}
              run={deleteAllRetailEvotorStoreProducts}
              forbiddenMessage={t('retailSettings.devOnlyForbidden')}
              disabled={!canManage}
            />
            <SyncTriggerRow
              label={t('retailSettings.sync.technicalSection.deleteGroupsLabel')}
              buttonLabel={t('retailSettings.sync.technicalSection.deleteGroupsButton')}
              destructive
              icon={<DeleteForeverRoundedIcon />}
              confirm={{
                title: t('retailSettings.sync.technicalSection.deleteGroupsConfirmTitle'),
                warning: t('retailSettings.sync.technicalSection.deleteWarning'),
              }}
              run={deleteAllRetailEvotorStoreProductGroups}
              forbiddenMessage={t('retailSettings.devOnlyForbidden')}
              disabled={!canManage}
            />
          </SettingsCard>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
